import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'
import { IdentifiedFieldIssue } from '../../IFITrackerContext/types'
import { CIMSPage, IFIStatus } from '@cims-online/shared'
import { useSuggestedIssueType } from './hooks/useSuggestedIssueType'
import { useReportID } from './hooks/useReportID'
import { useReportReviewStatus } from './hooks/useReportReviewStatus'
import { useReportDate } from './hooks/useReportDate'
import { useIssueDescription } from './hooks/useIssueDescription'
import { useSingleUserColumn } from 'components/CIMSTable/common/hooks/useSingleUserColumn'
import { HelpMessage } from 'utils/constants'
import MultiLineSpace from 'components/MultiLineSpace'
import { useIFITrackerContext } from '../../IFITrackerContext'
import { useStatus } from './hooks/useStatus'
import CIMSLink from 'components/CIMSLink'
import { Space } from 'antd'
import { useReportType } from './hooks/useReportType'
import { useProjectContext } from 'context/ProjectContext'

export function useColumns(): CIMSColumnsOptions<IdentifiedFieldIssue> {
  const help = HelpMessage.IdentifiedFieldIssues.IFITracker
  const { edit, refetch } = useIFITrackerContext()
  const { projectId } = useProjectContext()
  return {
    columns: [
      useStatus({ priority: 0 }),
      useReportID({ priority: 2 }),
      useReportType(),
      useReportReviewStatus(),
      useReportDate({ priority: 1 }),
      useSuggestedIssueType(),
      useIssueDescription(),
      useSingleUserColumn({
        title: 'Reviewed By',
        user: ({ reviewedBy }) => reviewedBy,
        help: help.ReviewedBy,
      }),
      {
        title: 'Review Notes',
        render: ({ reviewNotes }) => <MultiLineSpace text={reviewNotes} />,
        width: 200,
        help: help.ReviewNotes,
        filter: { type: 'search', with: 'key', key: 'reviewNotes' },
      },
      {
        title: 'Associated Records',
        render: ({ issues, engineeringJudgements }) => {
          const { module: issueModule, pathname: issuePathname } =
            CIMSPage.IssueTracker
          const { module: ejModule, pathname: ejPathname } = CIMSPage.EJLog
          if (!issues.length && !engineeringJudgements.length) return 'N/A'
          return (
            <Space size="small" direction="vertical">
              {issues.map(({ id, formattedNumber, description }) => (
                <CIMSLink
                  external={false}
                  tooltip={description}
                  to={`/${projectId}/${issueModule.pathname}/${issuePathname}/${id}`}
                >
                  {formattedNumber}
                </CIMSLink>
              ))}
              {engineeringJudgements.map(({ id, formattedNumber }) => (
                <CIMSLink
                  external={false}
                  to={`/${projectId}/${ejModule.pathname}/${ejPathname}/${id}`}
                >
                  {formattedNumber}
                </CIMSLink>
              ))}
            </Space>
          )
        },
        width: 250,
        help: HelpMessage.IdentifiedFieldIssues.AssociatedRecordNumber(
          'Record'
        ),
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, { issues }) =>
            issues.some(({ formattedNumber }) =>
              formattedNumber
                ?.toLowerCase()
                .includes(value.toString().toLowerCase())
            ),
        },
      },
    ],
    prominentCustomFields: {
      disableEditWhen: ({ statusId }) => ({
        alreadyDisabled:
          statusId === IFIStatus.Closed.id
            ? { why: 'invalid status', statusName: IFIStatus.Closed.name }
            : false,
      }),
      onCheckBoolean: async ({ reportId }, input) => {
        await edit({
          variables: {
            input: { reportId, customFields: { booleanInputs: [input] } },
          },
        })
        refetch()
      },
      onCheckBooleans: async ({ reportId }, input) => {
        await edit({
          variables: {
            input: {
              reportId,
              customFields: { listInputs: { checkboxes: [input] } },
            },
          },
        })
        refetch()
      },
    },
  }
}
